import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Search, UserSearch } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { shiftOpenDay, shortDate, skipSunday, toApiDate, todayIso } from "@/lib/fmt";

interface Participant {
  idCliente: number;
  nome: string;
  foto: string | null;
  oculto: boolean;
}

interface ClassRow {
  idConfiguracao: number;
  nome: string;
  horaInicio: string;
  professor: string | null;
  participants: Participant[];
}

interface StudentRow {
  idCliente: number;
  nome: string;
  turmas: string[];
  /** Participant asked not to appear in participation lists — the API returns them anyway. */
  oculto: boolean;
}

export function StudentsTab() {
  const [date, setDate] = useState(() => skipSunday(todayIso()));
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [revealed, setRevealed] = useState<Record<number, string>>({});

  const loadDay = useCallback(async (iso: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/agenda/${toApiDate(iso)}/participants`, { cache: "no-store" });
      const body = (await res.json().catch(() => null)) as { classes?: ClassRow[]; error?: string } | null;
      if (!res.ok) {
        setClasses([]);
        setError(body?.error ?? `falha ao carregar (HTTP ${res.status})`);
        return;
      }
      setClasses(body?.classes ?? []);
    } catch (e) {
      setClasses([]);
      setError(e instanceof Error ? e.message : "falha de rede");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDay(date);
  }, [date, loadDay]);

  const students = useMemo<StudentRow[]>(() => {
    const byId = new Map<number, StudentRow>();
    for (const cls of classes) {
      for (const p of cls.participants) {
        const row = byId.get(p.idCliente) ?? { idCliente: p.idCliente, nome: p.nome, turmas: [], oculto: false };
        row.turmas.push(`${cls.horaInicio} ${cls.nome}`);
        row.oculto = row.oculto || p.oculto;
        byId.set(p.idCliente, row);
      }
    }
    const all = [...byId.values()].sort((a, b) => a.nome.localeCompare(b.nome));
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((s) => s.nome.toLowerCase().includes(q) || String(s.idCliente) === q);
  }, [classes, query]);

  /** Ids flagged with `flOcultarDadosDeParticipacaoAgendamentoMobile` in the class roster. */
  const optOuts = useMemo(() => {
    const ids = new Set<number>();
    for (const cls of classes) {
      for (const p of cls.participants) if (p.oculto) ids.add(p.idCliente);
    }
    return ids;
  }, [classes]);

  /**
   * The roster answers "Anônimo" for opted-out members; the staff endpoints answer
   * the real name for the same id. Resolving them is the whole point of this PoC.
   */
  useEffect(() => {
    const pending = students.filter((s) => s.oculto && revealed[s.idCliente] === undefined).slice(0, 20);
    if (pending.length === 0) return;
    let alive = true;
    void (async () => {
      for (const student of pending) {
        try {
          const res = await fetch(`/api/staff/students/${student.idCliente}/identity`, { cache: "no-store" });
          if (!res.ok) continue;
          const body = (await res.json()) as { nomeCompleto?: string | null };
          if (!alive) return;
          if (body.nomeCompleto) {
            setRevealed((prev) => ({ ...prev, [student.idCliente]: body.nomeCompleto as string }));
          }
        } catch {
          // segue sem revelar; a lista mostra o que o roster devolveu
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [students, revealed]);

  const isToday = date === todayIso();
  const numericQuery = /^\d{3,}$/.test(query.trim());

  return (
    <div style={{ display: "grid", gap: "0.85rem" }}>
      <section className="cb-panel cb-rise">
        <div className="cb-panel__body" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div className="cb-row" style={{ gap: "0.35rem" }}>
            <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => setDate(shiftOpenDay(date, -1))} aria-label="Dia anterior">
              <ChevronLeft size={14} />
            </button>
            <button type="button" className={isToday ? "cb-btn cb-btn--sm" : "cb-btn cb-btn--sm cb-btn--primary"} onClick={() => setDate(skipSunday(todayIso()))}>
              Hoje
            </button>
            <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => setDate(shiftOpenDay(date, 1))} aria-label="Próximo dia">
              <ChevronRight size={14} />
            </button>
          </div>

          <DatePicker value={date} onChange={(v) => setDate(skipSunday(v))} className="w-[11rem]" id="cb-students-date" />

          <div className="cb-row" style={{ gap: "0.35rem", flex: 1, minWidth: "14rem" }}>
            <Search size={13} style={{ color: "var(--cb-muted)" }} />
            <input
              className="cb-input"
              placeholder="buscar por nome ou id do aluno"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && /^\d{3,}$/.test(query.trim())) {
                  window.location.hash = `aluno/${query.trim()}`;
                }
              }}
              style={{ flex: 1 }}
            />
            {numericQuery && (
              <button
                type="button"
                className="cb-btn cb-btn--sm cb-btn--primary"
                onClick={() => {
                  window.location.hash = `aluno/${query.trim()}`;
                }}
              >
                Abrir #{query.trim()}
              </button>
            )}
          </div>

          <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => void loadDay(date)} aria-label="Recarregar">
            <RefreshCw size={13} className={loading ? "animate-spin" : undefined} />
          </button>
        </div>
        <div
          className="cb-fade"
          style={{ padding: "0.5rem 1.1rem", borderTop: "1px solid var(--cb-border)", fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: error ? "var(--cb-error)" : "var(--cb-warning)" }}
        >
          {error
            ? error
            : numericQuery
              ? `abrindo dossiê do id ${query.trim()} — busca por nome cobre ${students.length} aluno(s) desta data`
              : `${students.length} aluno(s) agendados em ${shortDate(date)} · ${optOuts.size} marcado(s) para não aparecer nas listas — a API devolve todos (docs/evo-staff-api.md §2.5)`}
        </div>
      </section>

      <section className="cb-panel cb-rise-1">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <UserSearch size={13} />
            Alunos
          </span>
          <span className="cb-label">{students.length} · clique para abrir o perfil</span>
        </div>
        {loading && students.length === 0 ? (
          <div className="cb-empty">consultando alunos…</div>
        ) : students.length === 0 ? (
          <div className="cb-empty">
            <strong>Nenhum aluno encontrado</strong>
            <span>a busca por nome usa quem tem agendamento na data escolhida</span>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(20rem, 1fr))",
              gap: "0.2rem 1.5rem",
              padding: "0.6rem 1rem 0.9rem",
            }}
          >
            {students.map((s) => {
              const nome = revealed[s.idCliente] ?? s.nome;
              return (
                <button
                  key={s.idCliente}
                  type="button"
                  className="cb-row"
                  onClick={() => {
                    window.location.hash = `aluno/${s.idCliente}${s.oculto ? "/anon" : ""}`;
                  }}
                  style={{
                    gap: "0.6rem",
                    alignItems: "center",
                    background: "transparent",
                    border: 0,
                    borderRadius: 8,
                    padding: "0.4rem 0.5rem",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span className="cb-label" style={{ minWidth: "3.6rem" }}>
                    #{s.idCliente}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{nome}</span>
                  {s.oculto && (
                    <span
                      className="cb-chip"
                      style={{
                        color: "var(--cb-warning)",
                        background: "var(--cb-warning-dim)",
                        borderColor: "rgba(255,182,46,.35)",
                        fontSize: "0.62rem",
                      }}
                    >
                      anônimo
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <p className="cb-label" style={{ textAlign: "center", color: "var(--cb-subtle)" }}>
        leitura direta dos endpoints do app de staff com o token de aluno — CPF mascarado, nada é gravado em banco
      </p>
    </div>
  );
}
